import { useEffect, useState } from 'react';
import { RecordPanel } from './RecordPanel';
import { Switch } from '@/components/Switch';
import { endTime, jobMinutes, type RecordRights, type RecArea, type RecBooking, type RecCustomer, type RecOperator, type RecordsApi } from '@/lib/records';

/**
 * The customer's next booking, managed from the chat (Gary, 18 Oct, after Lauren's cancel needed Sentinel).
 * The same steps and rules as Sentinel's booking screens (review, 18 Oct): beds set the job length and there is
 * no price on a booking; area, day and time, then operator; an operator assigned is what confirms it. Changes go
 * through Sentinel's booking functions, so the customer gets the same WhatsApp and email. Past bookings live in
 * History with their invoices.
 */
type View = 'booking' | 'edit' | 'move' | 'cancel' | 'assign' | 'new';

const STATUS: Record<RecBooking['status'], string> = { pending: 'No operator yet', confirmed: 'Confirmed', completed: 'Done', cancelled: 'Cancelled' };
// Sentinel's cancel reasons and their codes (booking-manage cancel.ts, BookingCancelModal).
const REASONS: [string, string][] = [
  ['customer_request', 'Customer requested cancellation'], ['no_show', 'Customer no-show'], ['reschedule', 'Customer wants to reschedule'],
  ['weather', 'Weather conditions'], ['operator_unavailable', 'Operator unavailable'], ['equipment_issue', 'Equipment issue'],
  ['duplicate', 'Duplicate booking'], ['other', 'Other reason'],
];

/** The next three weeks, from today, as "YYYY-MM-DD". */
function nextDays(): string[] {
  const out: string[] = [];
  const d = new Date();
  for (let i = 0; i < 21; i++) {
    out.push(d.toLocaleDateString('en-CA'));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

export function BookingsPanel({ api, booking, customer, rights, onClose, onChanged, onLeadBooked }: {
  api: RecordsApi; booking: RecBooking | null; customer: RecCustomer; rights: RecordRights; onClose: () => void; onChanged: () => void;
  /** A lead's first booking (no customer yet): the panel closes and says so; the chat becomes the new customer's. */
  onLeadBooked?: (message: string) => void;
}) {
  const [b, setB] = useState<RecBooking | null>(booking);
  const [view, setView] = useState<View>(booking ? 'booking' : 'new');
  const [beds, setBeds] = useState(booking?.beds ?? 1);
  const [street, setStreet] = useState(booking?.street ?? customer.street);
  const [suburb, setSuburb] = useState(booking?.suburb ?? customer.suburb);
  const [city, setCity] = useState(booking?.city ?? customer.city);
  const [note, setNote] = useState(booking?.note ?? '');
  const [areas, setAreas] = useState<RecArea[]>([]);
  const [area, setArea] = useState<string | null>(booking?.territoryId ?? customer.territoryId);
  // The customer's area shows alone with Change; the full list only on Change or when they have none (Gary, 19 Oct).
  const [pickArea, setPickArea] = useState(false);
  const [date, setDate] = useState<string | null>(null);
  const [times, setTimes] = useState<string[] | 'loading' | null>(null);
  const [time, setTime] = useState<string | null>(null);
  const [ops, setOps] = useState<RecOperator[] | 'loading' | null>(null);
  const [operator, setOperator] = useState<string | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [why, setWhy] = useState('');
  const [tell, setTell] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const first = customer.firstName || customer.company || 'the customer';
  // A form asks before its typing is thrown away (mobile review, 19 Oct): what it held when it opened, against now.
  // Picking an operator is not typing, and the list picks one by itself.
  const form = JSON.stringify([beds, street, suburb, city, note, date, time, reason, why]);
  const [opened, setOpened] = useState({ view, form });
  if (opened.view !== view) setOpened({ view, form });
  const unsaved = view !== 'booking' && view !== 'assign' && !busy && form !== opened.form;

  useEffect(() => { void api.areas().then(setAreas).catch(() => setAreas([])); }, [api]);
  // The area defaults to the customer's, else the only one there is. One that takes no bookings is not kept.
  useEffect(() => {
    if (!areas.length) return;
    if (area && !areas.some((a) => a.id === area)) { setArea(areas.length === 1 ? areas[0]!.id : null); return; }
    if (!area && areas.length === 1) setArea(areas[0]!.id);
  }, [areas, area]);

  // Free times for the chosen day and area.
  useEffect(() => {
    if (!date || !area) { setTimes(null); return; }
    let gone = false;
    setTimes('loading'); setTime(null);
    api.slots(area, date).then((t) => { if (!gone) setTimes(t); }).catch(() => { if (!gone) setTimes([]); });
    return () => { gone = true; };
  }, [api, area, date]);

  // Who can work that time: for a new booking, a move or a change of operator.
  const opDate = view === 'assign' ? b?.date ?? null : date;
  const opTime = view === 'assign' ? b?.time ?? null : time;
  const opArea = view === 'assign' ? b?.territoryId ?? null : area;
  useEffect(() => {
    if (!opDate || !opTime || !opArea || (view !== 'new' && view !== 'move' && view !== 'assign')) { setOps(null); return; }
    let gone = false;
    setOps('loading');
    api.operators(opArea, opDate, opTime).then((list) => {
      if (gone) return;
      setOps(list);
      if (view === 'new') setOperator((cur) => (cur && list.some((o) => o.id === cur && o.available !== false) ? cur : list.find((o) => o.available !== false)?.id ?? null));
      if (view === 'assign') setOperator(list.find((o) => o.available !== false && !b?.operators.some((x) => x.id === o.id))?.id ?? null);
    }).catch(() => { if (!gone) setOps([]); });
    return () => { gone = true; };
  }, [api, opArea, opDate, opTime, view, b]);

  async function act(run: () => Promise<void>, message: string) {
    setBusy(true); setError(null);
    try {
      await run();
      if (!customer.id && onLeadBooked) { onChanged(); onLeadBooked(message); return; }
      setDone(message);
      const fresh = await api.nextBooking().catch(() => null);
      setB(fresh);
      onChanged();
      setView(fresh ? 'booking' : 'new');
      setTell(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not go through. Try again.');
    } finally {
      setBusy(false);
    }
  }
  const told = (what: string) => (tell ? `${what} ${first} gets the usual WhatsApp and email; the operator is emailed.` : `${what} Nobody was told.`);
  const back = b ? () => { setError(null); setTell(true); setView('booking'); } : undefined;
  const errorLine = error && <p className="chats-rec-error" role="alert">{error}</p>;

  const bedsCard = (
    <>
      <p className="chats-rec-label">Beds</p>
      <div className="chats-rec-card">
        <div className="chats-rec-line chats-rec-step">
          <span>Beds to clean<small>About {jobMinutes(beds)} minutes on site</small></span>
          <span className="chats-rec-stepper">
            <button type="button" aria-label="One bed less" disabled={beds <= 1} onClick={() => setBeds(beds - 1)}>−</button>
            <b>{beds}</b>
            <button type="button" aria-label="One bed more" onClick={() => setBeds(beds + 1)}>+</button>
          </span>
        </div>
      </div>
    </>
  );
  const whereFields = (
    <div className="chats-rec-form">
      <label>Street address<input className="chats-input" value={street} onChange={(e) => setStreet(e.target.value)} /></label>
      <div className="chats-rec-two">
        <label>Suburb<input className="chats-input" value={suburb} onChange={(e) => setSuburb(e.target.value)} /></label>
        <label>Town<input className="chats-input" value={city} onChange={(e) => setCity(e.target.value)} /></label>
      </div>
      <label>Note for the operator<textarea className="chats-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} /></label>
    </div>
  );
  const when = (
    <>
      <div className="chats-rec-dates" role="listbox" aria-label="Day">
        {nextDays().map((d) => (
          <button key={d} type="button" role="option" aria-selected={date === d} className={`chats-chip${date === d ? ' active' : ''}`} onClick={() => setDate(d)}>{day(d)}</button>
        ))}
      </div>
      {!date && <p className="chats-rec-hint">Pick a day to see the free times.</p>}
      {times === 'loading' && <p className="chats-rec-hint">Finding free times…</p>}
      {Array.isArray(times) && times.length === 0 && <p className="chats-rec-hint">No free times that day. Try another day.</p>}
      {Array.isArray(times) && times.length > 0 && (
        <div className="chats-rec-slots">
          {times.map((t) => <button key={t} type="button" className={`chats-chip${time === t ? ' active' : ''}`} onClick={() => setTime(t)}>{t}</button>)}
        </div>
      )}
    </>
  );
  const operatorList = (exclude: string[] = []) => (
    ops === 'loading' ? <p className="chats-rec-hint">Checking who is free…</p>
      : Array.isArray(ops) && ops.length === 0 ? <p className="chats-rec-hint">No operators for this area.</p>
        : Array.isArray(ops) && (
          <div className="chats-rec-card chats-rec-card--rows">
            {ops.filter((o) => !exclude.includes(o.id)).map((o) => (
              <button key={o.id} type="button" className="chats-rec-row" disabled={o.available === false} onClick={() => setOperator(o.id)}>
                <span className="chats-rec-row-main"><b>{o.name}</b><small>{o.available === false ? o.reason || 'Not free then' : 'Free then'}</small></span>
                <span className={`chats-rec-radio${operator === o.id ? ' chats-rec-radio--on' : ''}`} aria-hidden="true" />
              </button>
            ))}
          </div>
        )
  );
  // Sentinel's "don't send notifications" switch, the other way round so on means the customer hears.
  const tellRow = (
    <button type="button" className="chats-rec-tell" onClick={() => setTell(!tell)}>
      <span className="chats-rec-tell-text">Tell {first}<small>{tell ? 'WhatsApp and email, as Sentinel sends' : 'No WhatsApp or email this time'}</small></span>
      <Switch on={tell} label={`Tell ${first}`} />
    </button>
  );

  if (view === 'new') {
    const missing = !customer.email.trim() ? 'an email on the customer' : !(customer.mobile || customer.phone).trim() ? 'a mobile on the customer' : !area ? 'an area' : !street.trim() ? 'an address' : !date || !time ? 'a day and time' : null;
    return (
      <RecordPanel title="New booking" sub={[customer.firstName, customer.lastName].filter(Boolean).join(' ') || customer.company} onClose={onClose} onBack={back} unsaved={unsaved}
        footer={<button type="button" className="chats-btn chats-rec-wide" disabled={!!missing || busy}
          onClick={() => { if (!date || !time || !area) return; const d = date; const t = time;
            void act(() => api.create({ customer, territoryId: area, date: d, time: t, beds, street, suburb, city, note, operatorIds: operator ? [operator] : [], tell }).then(() => undefined), told(`Booked ${day(d)}, ${t}${operator ? '' : ', no operator yet'}.`)); }}>
          {busy ? 'Booking…' : missing ? `Needs ${missing}` : `Book ${day(date!)}, ${time}`}</button>}>
        {errorLine}
        <dl className="chats-rec-list chats-rec-list--tight">
          <div><dt>Mobile</dt><dd>{customer.mobile || customer.phone || 'None: add it under Customer'}</dd></div>
          <div><dt>Email</dt><dd>{customer.email || 'None: add it under Customer'}</dd></div>
        </dl>
        {bedsCard}
        <p className="chats-rec-label">Where</p>
        {whereFields}
        {areas.length > 1 && (
          <>
            <p className="chats-rec-label chats-rec-label--gap">Area</p>
            {area && !pickArea ? (
              <div className="chats-rec-card">
                <div className="chats-rec-line chats-rec-dd-act">
                  <span>{areas.find((a) => a.id === area)?.name}</span>
                  <button type="button" className="chats-info-act" onClick={() => setPickArea(true)}>Change</button>
                </div>
              </div>
            ) : (
              <div className="chats-rec-slots">
                {areas.map((a) => <button key={a.id} type="button" className={`chats-chip${area === a.id ? ' active' : ''}`} onClick={() => { setArea(a.id); setPickArea(false); }}>{a.name}</button>)}
              </div>
            )}
          </>
        )}
        <p className="chats-rec-label chats-rec-label--gap">When</p>
        {when}
        {time && <><p className="chats-rec-label chats-rec-label--gap">Operator</p>{operatorList()}</>}
        <div className="chats-rec-gap">{tellRow}</div>
      </RecordPanel>
    );
  }

  if (!b) return null;
  const opNames = b.operators.map((o) => o.name).join(', ');

  if (view === 'edit') {
    const patch = {
      ...(beds !== b.beds ? { beds } : {}),
      ...(street !== b.street ? { street } : {}),
      ...(suburb !== b.suburb ? { suburb } : {}),
      ...(city !== b.city ? { city } : {}),
      ...(note !== b.note ? { note } : {}),
    };
    const changed = Object.keys(patch).length > 0;
    return (
      <RecordPanel title="Edit booking" sub={`${b.code} · ${day(b.date)}, ${b.time}`} onClose={onClose} onBack={back} unsaved={unsaved}
        footer={<button type="button" className="chats-btn chats-rec-wide" disabled={!changed || !street.trim() || busy}
          onClick={() => void act(() => api.update(b, patch, tell), 'Saved.')}>{busy ? 'Saving…' : 'Save changes'}</button>}>
        {errorLine}
        {bedsCard}
        <p className="chats-rec-label">Where</p>
        {whereFields}
        <p className="chats-rec-hint">To change the day or time, use Move.</p>
      </RecordPanel>
    );
  }

  if (view === 'move') {
    // The booking keeps its operator, as in Sentinel: a time they are not free is shown and refused.
    const mine = Array.isArray(ops) ? ops.filter((o) => b.operators.some((x) => x.id === o.id)) : [];
    const busyOp = mine.find((o) => o.available === false);
    return (
      <RecordPanel title="Move booking" sub={`${b.code} · now ${day(b.date)}, ${b.time}`} onClose={onClose} onBack={back} unsaved={unsaved}
        footer={<button type="button" className="chats-btn chats-rec-wide" disabled={!date || !time || !!busyOp || ops === 'loading' || busy}
          onClick={() => { if (!date || !time) return; const d = date; const t = time; void act(() => api.update(b, { date: d, time: t }, tell), told(`Moved to ${day(d)}, ${t}.`)); }}>
          {busy ? 'Moving…' : date && time ? `Move to ${day(date)}, ${time}` : 'Pick a new day and time'}</button>}>
        {errorLine}
        <p className="chats-rec-hint">Free times in {b.area || 'this area'}{opNames ? `. ${opNames} stays on it.` : '.'}</p>
        {when}
        {busyOp && <p className="chats-rec-error">{busyOp.name} is not free then{busyOp.reason ? `: ${busyOp.reason}` : ''}. Pick another time, or change the operator first.</p>}
        <div className="chats-rec-gap">{tellRow}</div>
      </RecordPanel>
    );
  }

  if (view === 'assign') {
    const current = b.operators.map((o) => o.id);
    return (
      <RecordPanel title={current.length ? 'Change operator' : 'Assign operator'} sub={`${b.code} · ${day(b.date)}, ${b.time}`} onClose={onClose} onBack={back} unsaved={unsaved}
        footer={<button type="button" className="chats-btn chats-rec-wide" disabled={!operator || busy}
          onClick={() => { if (!operator) return; const id = operator; const name = Array.isArray(ops) ? ops.find((o) => o.id === id)?.name ?? 'The operator' : 'The operator';
            void act(() => api.assign(b, id, tell), `${name} is on it${tell ? ' and is emailed' : ''}. The booking is confirmed.`); }}>
          {busy ? 'Assigning…' : 'Assign'}</button>}>
        {errorLine}
        <p className="chats-rec-hint">Operators for {b.area || 'this area'} at {b.time}{opNames ? `. Now: ${opNames}.` : '.'}</p>
        {operatorList(current)}
        <div className="chats-rec-gap">
          <button type="button" className="chats-rec-tell" onClick={() => setTell(!tell)}>
            <span className="chats-rec-tell-text">Email the operator<small>{tell ? 'They get the job by email, as from Sentinel' : 'No email this time'}</small></span>
            <Switch on={tell} label="Email the operator" />
          </button>
        </div>
      </RecordPanel>
    );
  }

  if (view === 'cancel') {
    const label = REASONS.find(([code]) => code === reason)?.[1];
    return (
      <RecordPanel title="Cancel booking" sub={`${b.code} · ${day(b.date)}, ${b.time}`} onClose={onClose} onBack={back} unsaved={unsaved}
        footer={<button type="button" className="chats-btn chats-btn--danger chats-rec-wide" disabled={!reason || busy}
          onClick={() => { if (!reason) return; const r = reason; void act(() => api.cancel(b, r, why, tell), told(`Cancelled (${label}).`)); }}>{busy ? 'Cancelling…' : 'Cancel this booking'}</button>}>
        {errorLine}
        <p className="chats-rec-label">Why</p>
        <div className="chats-rec-slots chats-rec-slots--stack">
          {REASONS.map(([code, text]) => <button key={code} type="button" className={`chats-chip${reason === code ? ' active' : ''}`} onClick={() => setReason(code)}>{text}</button>)}
        </div>
        <div className="chats-rec-form chats-rec-gap">
          <label>Notes (optional)<textarea className="chats-input" rows={2} value={why} onChange={(e) => setWhy(e.target.value)} /></label>
        </div>
        <div className="chats-rec-gap">{tellRow}</div>
      </RecordPanel>
    );
  }

  const open = b.status === 'pending' || b.status === 'confirmed';
  const reset = () => { setError(null); setTell(true); setDate(null); setTime(null); setOperator(null); };
  // A cancelled booking (shown when nothing is ahead) books again with its beds, address and area filled in.
  const bookAgain = () => { reset(); setBeds(b.beds); setStreet(b.street); setSuburb(b.suburb); setCity(b.city); setNote(b.note); setArea(b.territoryId); setPickArea(false); setView('new'); };
  return (
    <RecordPanel title={`${day(b.date)}, ${b.time}`} sub={`${b.code} · ${STATUS[b.status]}`} onClose={onClose}
      footer={b.status === 'cancelled' && rights.book ? (
        <button type="button" className="chats-btn chats-rec-wide" onClick={bookAgain}>Book again</button>
      ) : open && (rights.book || rights.cancel) ? (
        <>
          {rights.book && <button type="button" className="chats-btn chats-btn--ghost" onClick={() => { reset(); setBeds(b.beds); setStreet(b.street); setSuburb(b.suburb); setCity(b.city); setNote(b.note); setView('edit'); }}>Edit</button>}
          {rights.book && <button type="button" className="chats-btn chats-btn--ghost" onClick={() => { reset(); setArea(b.territoryId); setView('move'); }}>Move</button>}
          {rights.cancel && <button type="button" className="chats-btn chats-btn--ghost chats-rec-danger" onClick={() => { reset(); setReason(null); setWhy(''); setView('cancel'); }}>Cancel</button>}
        </>
      ) : undefined}>
      {done && <p className="chats-rec-done">{done}</p>}
      {b.status === 'pending' && rights.assign && (
        <button type="button" className="chats-btn chats-rec-wide chats-rec-confirm" onClick={() => { reset(); setView('assign'); }}>Assign an operator to confirm</button>
      )}
      <dl className="chats-rec-list">
        <div><dt>When</dt><dd>{day(b.date)}, {b.time} to {b.end || endTime(b.time, b.beds)}</dd></div>
        <div><dt>Status</dt><dd><span className={`chats-rec-status chats-rec-status--${b.status}`}>{STATUS[b.status]}</span></dd></div>
        <div><dt>Beds</dt><dd>{b.beds}</dd></div>
        <div><dt>Where</dt><dd>{b.street || 'No address'}{(b.suburb || b.city) && <><br />{[b.suburb, b.city].filter(Boolean).join(', ')}</>}</dd></div>
        {b.area && <div><dt>Area</dt><dd>{b.area}</dd></div>}
        <div>
          <dt>Operator</dt>
          <dd className="chats-rec-dd-act">
            <span>{opNames || 'None yet'}</span>
            {open && rights.assign && <button type="button" className="chats-info-act" onClick={() => { reset(); setView('assign'); }}>{opNames ? 'Change' : 'Assign'}</button>}
          </dd>
        </div>
        <div><dt>Reference</dt><dd>{b.code}</dd></div>
      </dl>
      {b.note && <><p className="chats-rec-label">Note for the operator</p><p className="chats-rec-notes">{b.note}</p></>}
    </RecordPanel>
  );
}

/** "Thu 22 Oct". */
export function day(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' });
}
